import React, { useMemo } from 'react';
import { CalendarEvent, DragState } from '../../types/calendar';
import EventCard from './EventCard';

interface WeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  dragState: DragState;
  onDragStart: (event: CalendarEvent, position: { x: number; y: number }) => void;
  onDragMove: (position: { x: number; y: number }) => void;
  onDragEnd: () => void;
  onTimeSlotDoubleClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
}

const HOUR_HEIGHT = 60;            // גובה שורה לשעה
const TIME_COL_WIDTH = 72;         // רוחב עמודת השעות

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  dragState,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTimeSlotDoubleClick,
  onEventClick
}) => {
  // מייצר את 7 ימי השבוע שמכילים את currentDate
  const weekDays = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    // ב-he-IL ראשון הוא היום הראשון בשבוע (0), נזיז לתחילת שבוע
    const dayOfWeek = startOfWeek.getDay(); // 0..6 (א'..שבת)
    const diff = startOfWeek.getDate() - dayOfWeek; // להחזיר לא'
    startOfWeek.setDate(diff);

    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });
  }, [currentDate]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForDay = (day: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === day.toDateString();
    });
  };

  const getEventPosition = (event: CalendarEvent) => {
    const startHour = event.startTime.getHours();
    const startMinutes = event.startTime.getMinutes();
    return (startHour * HOUR_HEIGHT) + (startMinutes / 60) * HOUR_HEIGHT;
  };

  const handleTimeSlotDoubleClick = (day: Date, hour: number) => {
    const slotDate = new Date(day);
    slotDate.setHours(hour, 0, 0, 0);
    onTimeSlotDoubleClick(slotDate);
  };

  // תצוגת טקסט ליום (א׳, ב׳, ג׳ ...)
  const hebWeekday = (d: Date) =>
    d.toLocaleDateString('he-IL', { weekday: 'short' }).replace('.', '');

  const isToday = (d: Date) =>
    d.toDateString() === new Date().toDateString();

  return (
    <div
      className="flex-1 bg-white overflow-hidden flex flex-col"
      style={{ marginTop: '55px' }}
      dir="rtl"
    >
      {/* כותרת שבוע */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="grid" style={{ gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))` }}>
          {/* עמודת שעות */}
          <div className="p-3 text-center text-xs font-medium text-gray-600 border-l border-gray-200 flex items-center justify-center bg-gray-50">
            שעה
          </div>

          {/* ימי השבוע */}
          {weekDays.map((day, index) => (
            <div
              key={index}
              className={`p-2 border-l border-gray-200 flex items-center justify-center`}
            >
              <div className="flex flex-col items-center justify-center gap-1.5 leading-none">
                <span
                  className={`text-[10px] font-medium uppercase ${
                    isToday(day) ? 'text-blue-700' : 'text-gray-600'
                  }`}
                >
                  {hebWeekday(day)}
                </span>

                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                    ${isToday(day) ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}`}
                  aria-label={day.toLocaleDateString('he-IL')}
                >
                  {day.getDate()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* גריד זמנים לגלילה אנכית */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))`,
            height: `${24 * HOUR_HEIGHT}px`
          }}
        >
          {/* עמודת השעות */}
          <div className="border-l border-gray-200 bg-gray-50">
            {hours.map(hour => (
              <div
                key={hour}
                className="border-b border-gray-100 flex items-start pt-1 px-2"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="text-xs text-gray-600 font-normal">
                  {hour.toString().padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* עמודות הימים */}
          {weekDays.map((day, dayIndex) => (
            <div key={dayIndex} className="border-l border-gray-200 relative">
              {hours.map(hour => (
                <div
                  key={hour}
                  className="border-b border-gray-100 relative hover:bg-blue-50/30 transition-colors cursor-pointer"
                  style={{ height: `${HOUR_HEIGHT}px` }}
                  onDoubleClick={() => handleTimeSlotDoubleClick(day, hour)}
                />
              ))}

              {/* אירועים ביום */}
              <div className="absolute top-0 left-0 right-0 pointer-events-none">
                {getEventsForDay(day).map(event => (
                  <div
                    key={event.id}
                    className="pointer-events-auto"
                    style={{
                      position: 'absolute',
                      top: `${getEventPosition(event)}px`,
                      left: 4,
                      right: 4,
                    }}
                  >
                    <EventCard
                      event={event}
                      onDragStart={onDragStart}
                      onDragMove={onDragMove}
                      onDragEnd={onDragEnd}
                      onEventClick={onEventClick}
                      isDragging={dragState.isDragging && dragState.draggedEvent?.id === event.id}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
